<template>
    <transition name="fade">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-index-2">
        <div class="bg-white rounded-lg p-4 w-full max-w-lg">
          <form @submit.prevent="submitForm" enctype="multipart/form-data">
            <div class="mb-4">
              <label for="recinto" class="block text-sm font-medium text-gray-700">Recinto</label>
              <select id="recinto" v-model="form.id_recinto" class="input-style" required>
                <option v-for="recinto in recintos" :value="recinto.id_recinto" :key="recinto.id_recinto">
                  {{ recinto.nombre }}
                </option>
              </select>
            </div>
            <div class="mb-4">
              <label for="evento" class="block text-sm font-medium text-gray-700">Evento</label>
              <select id="evento" v-model="form.id_evento" class="input-style" required>
                <option v-for="evento in eventos" :value="evento.id_evento" :key="evento.id_evento">
                  {{ evento.nombre }}
                </option>
              </select>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700">Archivo Excel</label>
              <input type="file" accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @change="onExcelChange" class="input-style" :required="!form.archivo_excel && !isEdit" />
              <span v-if="form.archivo_excel && isEdit" class="block text-xs mt-1 text-gray-500">Archivo actual: {{ form.archivo_excel }}</span>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">SVG(s) y Etiqueta(s)</label>
              <div v-for="(svg, i) in svgInputs" :key="i" class="flex gap-2 mb-2 items-center">
                <input
                  type="text"
                  v-model="svg.label"
                  placeholder="Etiqueta (ej. Piso 1)"
                  class="input-style w-1/2"
                  :required="i === svgInputs.length - 1"
                >
                <input
                  type="file"
                  accept=".svg,image/svg+xml"
                  @change="onSVGFileChange($event, i)"
                  class="input-style w-1/2"
                  :required="!svg.filename && !svg.file" 
                >
                <button
                  type="button"
                  @click="removeSVGInput(i)"
                  v-if="svgInputs.length > 1"
                  class="text-red-500 font-bold"
                  title="Eliminar"
                >×</button>
                <span v-if="svg.filename && !svg.file" class="block text-xs mt-1 text-gray-500 ml-2">Actual: {{ svg.filename }}</span>
              </div>
              <button
                type="button"
                class="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-1 px-3 rounded mt-2"
                @click="addSVGInput"
              >+ Agregar otro SVG</button>
            </div>
            <div class="flex justify-end">
              <button type="button" @click="$emit('close')" class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">Cancelar</button>
              <button type="submit" class="ml-5 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                {{ isEdit ? 'Guardar Cambios' : 'Crear Asignación' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </template>
  
  <script setup>
  import { ref, watch, onMounted, computed, defineProps, defineEmits } from 'vue';
  import { useToast } from "vue-toastification";
  import { useStore } from 'vuex';
  
  const store = useStore();
  
  const props = defineProps({
    plano: { type: Object, default: null }
  });
  const emit = defineEmits(['close', 'saved']);
  const toast = useToast();
  
  const form = ref({
    id_plano: null,
    id_recinto: '',
    id_evento: '',
    archivo_excel: '',
    archivo_svg: ''
  });
  const recintos = ref([]);
  const eventos = ref([]);
  const svgInputs = ref([{ label: '', file: null, filename: '' }]);
  const isEdit = computed(() => !!form.value.id_plano);
  
  let excelFile = null;
  
  async function fetchRecintos() {
    const res = await fetch('/api/recintos');
    recintos.value = await res.json();
  }
  async function fetchEventos() {
    const token = store.state.user.token;
    const res = await fetch(`/api/data-eventos/${token}`);
    eventos.value = await res.json();
  }
  
  function onExcelChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xls','xlsx','csv'].includes(ext) &&
        !['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'].includes(file.type)) {
      toast.error("Solo archivos de Excel válidos");
      e.target.value = '';
      return;
    }
    excelFile = file;
  }
  
  function onSVGFileChange(e, i) {
    svgInputs.value[i].file = e.target.files[0] || null;

    // // Si cargas nuevo archivo, borra filename anterior para saber que será reemplazado
    // if (svgInputs.value[i].file) {
    //   svgInputs.value[i].filename = '';
    // }
  }
  
  function addSVGInput() {
    const last = svgInputs.value[svgInputs.value.length - 1];
    if (!last.label || (!last.file && !last.filename)) {
      toast.error('Completa la etiqueta y el archivo antes de agregar otro.');
      return;
    }
    svgInputs.value.push({ label: '', file: null, filename: '' });
  }
  
  function removeSVGInput(i) {
    svgInputs.value.splice(i, 1);
  }
  
  function fillSVGInputsFromProps() {
  if (props.plano && props.plano.archivo_svg) {
    let arr;
    try {
      arr = Array.isArray(props.plano.archivo_svg)
        ? props.plano.archivo_svg
        : JSON.parse(props.plano.archivo_svg);
    } catch {
      arr = [];
    }
    svgInputs.value = arr.map(x => ({
      label: x.label,
      file: null,
      filename: x.file || '',  // <-- aquí aseguras filename
    }));
    // Si NO es edición y la lista está vacía, agrega uno vacío
    if (!isEdit.value && svgInputs.value.length === 0) {
      svgInputs.value.push({ label: '', file: null, filename: '' });
    }
  } else {
    svgInputs.value = [{ label: '', file: null, filename: '' }];
  }
}

  
  function fillFormFromProps() {
    if (props.plano) {
      form.value = { ...props.plano };
    } else {
      form.value = { id_plano: null, id_recinto: '', id_evento: '', archivo_excel: '', archivo_svg: '' };
    }
    excelFile = null;
  }
  
  // Watch correcto para cargar datos de edición
  watch(
    () => props.plano,
    () => {
      fillFormFromProps();
      fillSVGInputsFromProps();
    },
    { immediate: true }
  );
  
  onMounted(() => {
    fetchRecintos();
    fetchEventos();
  });
  


  async function submitForm() {
      // Si no hay ningún SVG (nuevo ni viejo), exige al menos uno
      if (svgInputs.value.length === 0 || svgInputs.value.every(x => !x.label)) {
        toast.error('Debes agregar al menos un SVG.');
        return;
      }
      // Todos los SVG deben tener etiqueta (pero no archivo nuevo forzoso)
      for (let svg of svgInputs.value) {
        if (!svg.label) {
          toast.error('Completa la etiqueta de todos los SVGs antes de guardar.');
          return;
        }
      }

      const fd = new FormData();
      fd.append('id_recinto', form.value.id_recinto);
      fd.append('id_evento', form.value.id_evento);
      fd.append('tipo', form.value.tipo || 'stand');
      if (excelFile) fd.append('archivo_excel', excelFile);

      svgInputs.value.forEach((svg, i) => {
        // Creamos un JSON con sus campos
        const item = {
          label: svg.label,
          old_filename: svg.filename || '',
          // No podemos incluir file en JSON, así que usamos el mismo índice para files
          index: i
        };
        fd.append('svg_records[]', JSON.stringify(item));
        if (svg.file) {
          fd.append(`svg_file_${i}`, svg.file);
        }
      });


      let url = '/api/planos-evento';
      let method = 'POST';
      if (isEdit.value) {
        url = `/api/planos-evento/update/${form.value.id_plano}`;
      }

      fetch(url, {
        method: method,
        body: fd
      })
        .then(res => res.json())
        .then(() => {
          toast.success(isEdit.value ? "Asignación actualizada" : "Asignación creada");
          emit('saved');
          emit('close');
        })
        .catch(() => {
          toast.error("Error al guardar la asignación");
        });
    }

  </script>
  
  <style lang="sass" scoped>
  .input-style
    display: block
    width: 100%
    padding: 0.625rem
    border: 1px solid #cccccc
    border-radius: 0.375rem
    box-shadow: inset 0 1px 1px rgba(0,0,0,0.075)
    &:focus
      outline: none
      border-color: #3182ce
      ring: #3182ce
  
  .fade-enter-active, .fade-leave-active
    transition: opacity 0.5s
  .fade-enter, .fade-leave-to
    opacity: 0
  .z-index-2
    z-index: 2
  </style>
  